// Pure helpers of the Settings tab's Topology and Firewall sections (feature spec 17, phase 2; spec 11 6.15.12).
import type { NetworkKinds, ProviderNetworkSettings } from '../../api/types';

export type FirewallForm = NonNullable<ProviderNetworkSettings['firewall']>;

/** The cheapest sensible firewall price per region of the network's providers ("From about USD 912/month"). */
export function firewallFromCost(
  firewalls: NetworkKinds['firewalls'],
  providers: readonly string[],
): string | null {
  for (const provider of providers) {
    const costs = firewalls.find((f) => f.provider === provider && f.status === 'available')?.tierCosts;
    const cost = costs?.standard ?? Object.values(costs ?? {})[0];
    // Cloud NGFW Standard (phase 6) has no fixed fee: hub topologies cost nothing extra for its firewall.
    if (cost && cost.monthly > 0) return `${cost.currency} ${cost.monthly.toLocaleString('en-US')}`;
  }
  return null;
}

/** The firewall entries the Settings tab offers: available ones of the network's providers, and configured ones. */
export function offeredFirewalls(
  firewalls: NetworkKinds['firewalls'],
  providers: readonly string[],
  configured: readonly string[],
): NetworkKinds['firewalls'] {
  return firewalls.filter(
    (f) => providers.includes(f.provider) && (f.status === 'available' || configured.includes(f.provider)),
  );
}

/**
 * AWS central egress (feature spec 17, phase 4): the central NAT gateways' list price per region and month
 * (`network-costs.json` item `central-egress`, spec 18 5.10). The registry carries only the firewall's tier price, so the
 * form names this one; the impact panel shows the priced rows of the dry run.
 */
export const AWS_CENTRAL_EGRESS = { monthly: 66, currency: 'USD' } as const;

/** AWS transit gateway attachments' list price per attachment and month (`network-costs.json` item `tgw-attachment`). */
export const AWS_TGW_ATTACHMENT = { monthly: 37, currency: 'USD' } as const;

/** The largest hub prefix length that holds the central egress subnets (spec 09 6.16: /25 and larger hubs). */
export const AWS_CENTRAL_EGRESS_MAX_HUB_PREFIX = 25;

/**
 * Google Cloud (feature spec 17, phase 6; spec 18 5.10 *(verify V10)*): Cloud NAT per spoke that needs it and the NCC
 * VPC spoke per attachment, list prices per month. The registry carries only the firewall's tier prices, so the form
 * names these; the impact panel shows the priced rows of the dry run.
 */
export const GCP_CLOUD_NAT = { monthly: 32, currency: 'USD' } as const;
export const GCP_NCC_SPOKE = { monthly: 37, currency: 'USD' } as const;

export const money = (cost: { monthly: number; currency: string }) =>
  `${cost.currency} ${cost.monthly.toLocaleString('en-US')}`;
